import { useEffect, useMemo, useState } from "react";

import {
  getAccountingProfile,
  updateAccountingProfile,
} from "../../api/accounting";
import { useAuth } from "../../context/AuthContext";
import SalonBusinessCpaShell from "./SalonBusinessCpaShell";

const profileDefaults = {
  employee_beauty_professional: ["needs_personal_tax"],
  independent_professional_sole_proprietor: [
    "needs_personal_tax",
    "needs_business_tax",
    "needs_bookkeeping",
  ],
  independent_professional_incorporated: [
    "needs_personal_tax",
    "needs_corporate_tax",
    "needs_bookkeeping",
    "needs_financial_statements",
    "needs_dividends_shareholder_loans",
  ],
  salon_owner_sole_proprietor: [
    "needs_personal_tax",
    "needs_business_tax",
    "needs_gst_hst",
    "needs_payroll",
    "needs_bookkeeping",
  ],
  salon_owner_incorporated: [
    "needs_personal_tax",
    "needs_corporate_tax",
    "needs_gst_hst",
    "needs_payroll",
    "needs_bookkeeping",
    "needs_financial_statements",
    "needs_dividends_shareholder_loans",
  ],
};

function makeDefaultFlags(profileCode, moduleOptions = []) {
  const defaults = new Set(profileDefaults[profileCode] || []);
  return moduleOptions.reduce((next, option) => {
    next[option.code] = defaults.has(option.code);
    return next;
  }, {});
}

export function AccountingProfileForm({ profile, onSaved }) {
  const { token } = useAuth();
  const profileOptions = useMemo(
    () =>
      Array.isArray(profile?.client_profile_options)
        ? profile.client_profile_options
        : [],
    [profile]
  );
  const moduleOptions = useMemo(
    () => (Array.isArray(profile?.module_options) ? profile.module_options : []),
    [profile]
  );
  const [profileCode, setProfileCode] = useState(profile?.client_profile_code || "");
  const [moduleFlags, setModuleFlags] = useState(profile?.module_flags || {});
  const [notes, setNotes] = useState(profile?.notes || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setProfileCode(profile?.client_profile_code || "");
    setModuleFlags(profile?.module_flags || {});
    setNotes(profile?.notes || "");
  }, [profile]);

  const selectedProfile = useMemo(
    () => profileOptions.find((option) => option.code === profileCode),
    [profileCode, profileOptions]
  );

  const handleProfileChange = (nextProfileCode) => {
    setProfileCode(nextProfileCode);
    setModuleFlags(makeDefaultFlags(nextProfileCode, moduleOptions));
  };

  const toggleModule = (code) => {
    setModuleFlags((current) => ({
      ...current,
      [code]: !current?.[code],
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!profileCode) {
      setError("Choose the profile that best matches your work.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const saved = await updateAccountingProfile(token, {
        client_profile_code: profileCode,
        module_flags: moduleFlags,
        notes,
      });
      onSaved?.(saved);
    } catch (err) {
      setError(
        err?.response?.data?.detail || "Unable to save your SalonBusinessCPA profile."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error ? (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
          {error}
        </div>
      ) : null}

      <section className="grid gap-3 lg:grid-cols-5">
        {profileOptions.map((option) => {
          const active = option.code === profileCode;
          return (
            <button
              key={option.code}
              type="button"
              onClick={() => handleProfileChange(option.code)}
              className={`rounded-2xl border p-4 text-left transition ${
                active
                  ? "border-slate-950 bg-slate-950 text-white"
                  : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
              }`}
            >
              <span className="block text-sm font-semibold">{option.label}</span>
              <span
                className={`mt-2 block text-xs leading-5 ${
                  active ? "text-slate-200" : "text-slate-500"
                }`}
              >
                {option.description}
              </span>
            </button>
          );
        })}
      </section>

      <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-950">
              Services and situations
            </h2>
            <p className="mt-2 text-sm leading-6 text-slate-600">
              Select the areas you want SalonBusinessCPA to help organize. We start
              with the usual items for your profile, and you can adjust them.
            </p>
          </div>
          {selectedProfile ? (
            <span className="rounded-full bg-rose-50 px-3 py-1 text-xs font-semibold text-rose-700">
              {selectedProfile.label}
            </span>
          ) : null}
        </div>

        <div className="mt-5 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {moduleOptions.map((option) => (
            <label
              key={option.code}
              className="flex min-h-[56px] cursor-pointer items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700"
            >
              <input
                type="checkbox"
                checked={Boolean(moduleFlags?.[option.code])}
                onChange={() => toggleModule(option.code)}
                className="h-4 w-4 rounded border-slate-300 text-rose-500 focus:ring-rose-500"
              />
              <span className="font-medium">{option.label}</span>
            </label>
          ))}
        </div>
      </section>

      <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        <label className="block text-sm font-semibold text-slate-950" htmlFor="accounting-profile-notes">
          Notes for your accountant
        </label>
        <textarea
          id="accounting-profile-notes"
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
          rows={4}
          className="mt-3 w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm text-slate-700 outline-none focus:border-rose-300 focus:ring-2 focus:ring-rose-100"
          placeholder="Example: I have one location, I pay myself dividends, and I want help with GST/HST."
        />
      </section>

      <div className="flex flex-wrap items-center gap-3">
        <button
          type="submit"
          disabled={saving}
          className="rounded-full bg-rose-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-rose-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {saving ? "Saving..." : "Save profile"}
        </button>
        <p className="text-sm text-slate-500">
          This controls which checklists and document suggestions appear first.
        </p>
      </div>
    </form>
  );
}

export default function SalonBusinessCpaProfileIntake() {
  const { token } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) return;
    let active = true;
    setLoading(true);
    setError("");
    getAccountingProfile(token)
      .then((data) => {
        if (active) setProfile(data);
      })
      .catch((err) => {
        if (active) {
          setError(
            err?.response?.data?.detail || "Unable to load your SalonBusinessCPA profile."
          );
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [token]);

  return (
    <SalonBusinessCpaShell
      title="Accounting profile"
      description="Tell us which type of salon or beauty professional you are so the portal shows the right tax, payroll, bookkeeping, and document steps."
    >
      {loading ? (
        <div className="rounded-3xl border border-slate-200 bg-white p-6 text-sm text-slate-500">
          Loading profile...
        </div>
      ) : error ? (
        <div className="rounded-3xl border border-rose-200 bg-rose-50 p-6 text-sm text-rose-700">
          {error}
        </div>
      ) : (
        <AccountingProfileForm profile={profile} onSaved={setProfile} />
      )}
    </SalonBusinessCpaShell>
  );
}
